import { useEffect, useState } from "react";

import { SubscriptionPlans } from "@prisma/client";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";

import Button from "@musicfy/components/Button";
import StartCheckoutFlowButton from "@musicfy/components/PaymentButtons/StartCheckoutFlowButton";
import {
  FREE_MUSIC_DAILY,
  getSongQuotaDisplay,
} from "@musicfy/contants/Quotas";
import {
  hasMinimumSubscriptionPlan,
  getFormattedPrice,
  PriceId,
} from "@musicfy/contants/Subscriptions";
import { useConvertUsageCounterContext } from "@musicfy/libs/ConvertUsageCounterProvider";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { api } from "@musicfy/utils";

import Divider from "../Divider";
import { FEATURE_ROUTES } from "../Navigation/constants";

const UsageCountdownTimer = ({ ttl }: { ttl: number }): JSX.Element | null => {
  const [timeLeft, setTimeLeft] = useState(ttl);

  useEffect(() => {
    setTimeLeft(ttl);
  }, [ttl]);

  useEffect(() => {
    if (timeLeft <= 0) {
      return;
    }

    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTimeLeft) => prevTimeLeft - 1);
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  }, [timeLeft]);

  if (timeLeft <= 0) {
    return null;
  }

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = Math.floor(timeLeft % 60);

  return (
    <div className="text-14">
      Refreshes in{" "}
      <span className="font-semibold">
        {hours > 0 && `${hours}h `}
        {minutes > 0 && `${minutes}m `}
        {minutes === 0 && hours > 0 && `0m `}
        {seconds > 0 && `${seconds.toString().padStart(2, "0")}s`}
      </span>
    </div>
  );
};

interface QuotaCardProps {
  remaining: number;
  limit: number;
  period: "daily" | "monthly";
  unit: string;
  subtitle: string;
  exhaustedSubtitle?: string;
  availableOrigin: string;
  exhaustedOrigin: string;
  ttl: number;
  isUnauthenticated: boolean;
}

const QuotaCard = ({
  remaining,
  limit,
  period,
  unit,
  subtitle,
  exhaustedSubtitle,
  availableOrigin,
  exhaustedOrigin,
  ttl,
  isUnauthenticated,
}: QuotaCardProps): JSX.Element => {
  if (remaining > 0) {
    return (
      <div className="py-5 px-8 bg-violet/40 border border-white/15 rounded-12">
        <div className="text-20 font-medium leading-snug">
          {remaining.toLocaleString("en-US")} of {limit.toLocaleString("en-US")}{" "}
          {period} {unit} left
        </div>
        <div className="text-white/80 mt-2 text-14">{subtitle}</div>
        {isUnauthenticated ? (
          <Button fullWidth path={FEATURE_ROUTES.signup} className="mt-6">
            Create Account
          </Button>
        ) : (
          <StartCheckoutFlowButton
            origin={availableOrigin}
            fullWidth
            className="mt-6"
          >
            View Subscription Plans
          </StartCheckoutFlowButton>
        )}
      </div>
    );
  }

  return (
    <div className="py-5 px-8 bg-violet/40 border border-white/15 rounded-12">
      <div className="text-20 font-medium leading-snug mb-2">
        You&apos;ve reached your {period} {unit} limit
      </div>
      {period === "daily" ? (
        <UsageCountdownTimer ttl={ttl} />
      ) : (
        <div className="text-14">Refreshes with your monthly allowance</div>
      )}
      <Divider className="my-2" />
      <div className="text-white/80 mt-2 text-14">
        {exhaustedSubtitle ?? subtitle}
      </div>
      <div className="flex items-center mt-6 justify-between">
        <StartCheckoutFlowButton origin={exhaustedOrigin} className="!text-14">
          View Subscription Plans
        </StartCheckoutFlowButton>
      </div>
    </div>
  );
};

const NoticeBanner = (): JSX.Element | null => {
  const router = useRouter();
  const {
    pricesContext: { pricesData },
  } = usePaddleContext();
  const { subscription, isLoading: isSubscriptionLoading } =
    useSubscriptionContext();
  const {
    inferenceQuota: { limit },
    remainingGenerations,
    ttl: convertTtl,
  } = useConvertUsageCounterContext();

  const { status } = useSession();
  const [mounted, setMounted] = useState(false);
  const [showStudioUpsell, setShowStudioUpsell] = useState(false);
  const isSongPage = router.pathname === "/create/song";
  const isMusicPage = router.pathname === "/create/text-to-music";
  const isConvertPage = router.pathname === "/create/[...voice]";
  const songQuota = getSongQuotaDisplay(subscription?.plan);
  const { data: songUsage } = api.songGenerations.getRemainingUsage.useQuery(
    undefined,
    {
      enabled: mounted && isSongPage && status === "authenticated",
      refetchOnWindowFocus: false,
    }
  );
  const { data: musicUsage } = api.musicGenerations.getRemainingUsage.useQuery(
    undefined,
    {
      enabled:
        mounted &&
        isMusicPage &&
        status === "authenticated" &&
        !isSubscriptionLoading &&
        (!subscription || subscription.plan === SubscriptionPlans.free),
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  const hasLimitOnInference = limit !== Infinity;
  // Each branch below is a semantically different wall, so each passes its own
  // origin — the analytics `reason` is derived from it (see libs/analytics/paywall.ts).

  const canUpgradeToStudio = !hasMinimumSubscriptionPlan(
    subscription?.plan,
    SubscriptionPlans.studio
  );

  useEffect(() => {
    if (canUpgradeToStudio) {
      setShowStudioUpsell(Math.random() > 0.5);
    } else {
      setShowStudioUpsell(false);
    }
  }, [canUpgradeToStudio]);

  // Sidebar only mounts after client portal target exists; still wait one frame
  // so quota/session-driven CTA swaps (<a> vs <button>) never hit hydration.
  if (!mounted) {
    return null;
  }

  const isUnauthenticated = status === "unauthenticated";

  if (
    status === "authenticated" &&
    isSubscriptionLoading &&
    (isSongPage || isMusicPage)
  ) {
    return null;
  }

  if (isSongPage) {
    const songLimit = songUsage?.limit ?? songQuota.limit;
    const songPeriod = songUsage?.period ?? songQuota.period;
    const remainingSongs =
      status === "authenticated"
        ? songUsage?.remaining ?? songLimit
        : songLimit;
    return (
      <QuotaCard
        remaining={remainingSongs}
        limit={songLimit}
        period={songPeriod}
        unit="songs"
        subtitle="Voice targeting is available on Pro and Studio."
        exhaustedSubtitle="Upgrade for more songs and Voice Targeting."
        availableOrigin="song-quota-sidebar"
        exhaustedOrigin="song-limit-sidebar"
        ttl={songUsage?.ttl ?? 0}
        isUnauthenticated={isUnauthenticated}
      />
    );
  }

  const isFreePlan =
    !subscription || subscription.plan === SubscriptionPlans.free;

  if (isMusicPage && (isUnauthenticated || isFreePlan)) {
    const musicLimit = musicUsage?.limit ?? FREE_MUSIC_DAILY;
    const remainingMusic =
      status === "authenticated"
        ? musicUsage?.remaining ?? musicLimit
        : musicLimit;

    return (
      <QuotaCard
        remaining={remainingMusic}
        limit={musicLimit}
        period="daily"
        unit="music generations"
        subtitle="Create instrumental loops and backing beds."
        exhaustedSubtitle="Upgrade to keep generating music."
        availableOrigin="music-quota-sidebar"
        exhaustedOrigin="music-limit-sidebar"
        ttl={musicUsage?.ttl ?? 0}
        isUnauthenticated={isUnauthenticated}
      />
    );
  }

  if (isConvertPage && hasLimitOnInference) {
    return (
      <QuotaCard
        remaining={isUnauthenticated ? limit : remainingGenerations}
        limit={limit}
        period="daily"
        unit="conversions"
        subtitle="Train your own custom voice model."
        availableOrigin="voice-clone-sidebar"
        exhaustedOrigin="gen-limit-sidebar"
        ttl={convertTtl}
        isUnauthenticated={isUnauthenticated}
      />
    );
  }

  const { price: studioPrice } = getFormattedPrice(
    PriceId.studio_year,
    pricesData,
    "month"
  );

  if (showStudioUpsell) {
    return (
      <div className="py-5 px-8 bg-violet/40 border border-white/15 rounded-12">
        <div className="text-20 capitalize font-medium">
          Get 15 Custom Voice Models + Unlock All Voices
        </div>
        <div className="text-white/80 mt-2 text-14 leading-normal">
          Upgrade to Studio plan Starting for {studioPrice} only
        </div>
        <StartCheckoutFlowButton
          origin="studio-upsell-sidebar"
          fullWidth
          className="mt-6"
        >
          View Subscription Plans
        </StartCheckoutFlowButton>
      </div>
    );
  }

  return (
    <div className="py-5 px-8 bg-violet/40 border border-white/15 rounded-12">
      <div className="text-20 capitalize font-medium">
        Earn 25% on all paid referrals
      </div>
      <div className="text-white/80 mt-2 text-14">
        Start sharing and maximize your rewards today!
      </div>
      <Button fullWidth path={FEATURE_ROUTES.affiliate} className="mt-6">
        View Affliliate Program
      </Button>
    </div>
  );
};

export default NoticeBanner;
